import apiClient, {
  tokenStorage,
  impersonationStorage,
  type ImpersonationOriginal,
} from '@/shared/apiClient';
import { loadUser, storeUser } from '@/auth/session';
import type { AuthUser, UserRole } from '@/types';

interface ImpersonateResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  acting_as: {
    id: string;
    email: string;
    role: UserRole;
  };
}

export async function startImpersonation(tenantId: string): Promise<ImpersonateResponse> {
  const currentAccess = tokenStorage.getAccess();
  const currentUser = loadUser();

  if (!currentAccess || !currentUser) {
    throw new Error('Nicht authentifiziert');
  }

  const resp = await apiClient.post<ImpersonateResponse>(
    `/admin/tenants/${tenantId}/impersonate`,
  );

  // Benutzerobjekt des impersonierten Nutzers vorab bauen,
  // damit es zusammen mit dem Access-Token im SessionStorage gespeichert werden kann.
  const actingAsUser: AuthUser = {
    id: resp.data.acting_as.id,
    email: resp.data.acting_as.email,
    role: resp.data.acting_as.role,
    tenantId: resp.data.tenant.id,
    requires2fa: false,
  };

  const original: ImpersonationOriginal = {
    access: currentAccess,
    user: currentUser,
    // Impersonation-Access-Token + Acting-User werden für Rehydration nach
    // Page-Reload im sessionStorage mitgespeichert (PLAN.md „Impersonation-Refresh").
    impersonatedAccess: resp.data.access_token,
    impersonatedUser: actingAsUser,
  };
  impersonationStorage.begin(original);

  // Neue Tenant-Tokens setzen; kein Refresh-Token beim Impersonation-Flow.
  tokenStorage.set(resp.data.access_token, '');
  storeUser(actingAsUser);

  return resp.data;
}

export function endImpersonation(): AuthUser | null {
  const original = impersonationStorage.end();
  if (!original) {
    return null;
  }
  tokenStorage.set(original.access);
  storeUser(original.user);
  return original.user;
}
